import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatDateTime } from "@/lib/format";
import { 
  User, Store, Calendar, CreditCard, Banknote, Receipt, 
  Package, Phone, Clock, CheckCircle2, XCircle, AlertTriangle,
  Pencil, RotateCcw, Printer,
} from "lucide-react";

// WhatsApp icon component
function WhatsAppIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="#25D366" aria-hidden="true">
      <path d="M20.52 3.48A11.78 11.78 0 0012 0C5.4 0 .03 5.37.03 12c0 2.11.55 4.17 1.6 5.99L0 24l6.18-1.62A11.96 11.96 0 0012 24c6.63 0 12-5.37 12-12 0-3.2-1.25-6.21-3.48-8.52zM12 21.82a9.79 9.79 0 01-5-1.37l-.36-.21-3.67.96.98-3.58-.23-.37A9.81 9.81 0 1121.82 12 9.83 9.83 0 0112 21.82zm5.36-7.34c-.29-.15-1.73-.85-2-.95-.27-.1-.46-.15-.66.15-.2.29-.76.95-.94 1.14-.17.2-.35.22-.64.07-.29-.15-1.23-.45-2.34-1.44-.86-.77-1.45-1.72-1.62-2.01-.17-.29-.02-.45.13-.6.13-.13.29-.34.43-.51.15-.17.2-.29.29-.49.1-.2.05-.37-.02-.51-.07-.15-.66-1.59-.9-2.18-.24-.58-.48-.5-.66-.51h-.56c-.2 0-.51.07-.78.37-.27.29-1.02 1-1.02 2.43 0 1.43 1.04 2.81 1.18 3 .15.2 2.04 3.13 4.96 4.39.69.3 1.23.48 1.65.62.69.22 1.32.19 1.82.12.55-.08 1.73-.71 1.97-1.39.24-.68.24-1.27.17-1.39-.07-.12-.27-.2-.56-.34z" />
    </svg>
  );
}

// Payment mode icon helper
function getPaymentIcon(mode) {
  switch (mode?.toLowerCase()) {
    case "cash":
      return <Banknote className="w-3.5 h-3.5 text-green-600" />;
    case "upi":
      return <Phone className="w-3.5 h-3.5 text-purple-600" />;
    case "card":
      return <CreditCard className="w-3.5 h-3.5 text-blue-600" />;
    case "credit":
      return <Clock className="w-3.5 h-3.5 text-orange-600" />;
    default:
      return <Receipt className="w-3.5 h-3.5" />;
  }
}

// Status badge component
function SaleStatusBadge({ status, paymentStatus }) {
  const isCanceled = status === "canceled";
  const isPaid = paymentStatus === "paid";
  const isPartial = paymentStatus === "partial";
  
  if (isCanceled) {
    return <Badge variant="destructive" className="gap-1"><XCircle className="w-3 h-3" /> Cancelled</Badge>;
  }
  if (isPaid) {
    return <Badge className="bg-green-600 gap-1"><CheckCircle2 className="w-3 h-3" /> Paid</Badge>;
  }
  if (isPartial) {
    return <Badge className="bg-orange-500 gap-1"><AlertTriangle className="w-3 h-3" /> Partial</Badge>;
  }
  return <Badge variant="outline" className="gap-1"><Clock className="w-3 h-3" /> Unpaid</Badge>;
}

export function SaleDetailDialog({ sale, onOpenChange, onEdit, onReturn, onCancel }) {
  const open = !!sale;

  if (!open) return null;

  const total = Number(sale.total || 0);
  const amountPaid = Number(sale.amount_paid || 0);
  const outstanding = Math.max(0, total - amountPaid);
  const isCanceled = sale.status === "canceled";
  const lineItems = sale.line_items || [];
  const payments = sale.payments || [];
  const returns = sale.returns || [];
  const refundTotal = Number(sale.refund_total || sale.total_returned_amount || 0);

  const handlePrint = () => {
    window.open(`/sales/${sale.id}/print`, "_blank", "noopener");
  };

  const handleWhatsApp = () => {
    window.open(`/sales/${sale.id}/print?share=whatsapp`, "_blank", "noopener");
  };

  const handleEdit = () => {
    onOpenChange(false);
    onEdit?.(sale);
  };

  const handleReturn = () => {
    onOpenChange(false);
    onReturn?.(sale);
  };

  const handleCancel = () => {
    onOpenChange(false);
    onCancel?.(sale);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="sale-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              {sale.invoice_number || sale.id?.slice(0, 8).toUpperCase()}
            </span>
            <SaleStatusBadge status={sale.status} paymentStatus={sale.payment_status} />
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Header Info */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="w-4 h-4" />
              <span>{formatDateTime(sale.created_at)}</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Store className="w-4 h-4" />
              <span>{sale.shop_name || "—"}</span>
            </div>
            {sale.customer_name && (
              <div className="flex items-center gap-2 col-span-2">
                <User className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium">{sale.customer_name}</span>
                {sale.customer_phone && (
                  <span className="text-muted-foreground text-xs">({sale.customer_phone})</span>
                )}
              </div>
            )}
            {sale.created_by_name && (
              <div className="flex items-center gap-2 col-span-2 text-muted-foreground text-xs">
                <span>Billed by: {sale.created_by_name}</span>
              </div>
            )}
          </div>

          {/* Line Items */}
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-muted/50 px-3 py-2 text-xs font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Package className="w-4 h-4" />
              Items ({lineItems.length})
            </div>
            <div className="divide-y">
              {lineItems.map((item, idx) => {
                const productName = item.product_name || item.product_snapshot?.product || item.manual_name || "Unknown";
                const qty = item.quantity || 1;
                const mrp = Number(item.mrp || 0);
                const subtotal = Number(item.subtotal || mrp * qty);
                const returned = item.returned_quantity || 0;
                const autoBarcode = item.product_snapshot?.auto_barcode || "";
                const manualBarcode = item.product_snapshot?.manual_barcode || "";
                
                return (
                  <div key={item.id || idx} className="px-3 py-2 flex justify-between items-start text-sm">
                    <div className="flex-1">
                      <div className="font-medium flex items-center gap-2">
                        {productName}
                        {/* Show MANUAL badge if no barcodes */}
                        {!autoBarcode && !manualBarcode && (
                          <Badge 
                            variant="outline" 
                            className="text-[9px] px-1.5 py-0 h-4 bg-orange-50 text-orange-700 border-orange-200"
                            data-testid={`line-item-manual-badge-${idx}`}
                          >
                            MANUAL
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {qty} × ₹{mrp.toFixed(2)}
                        {returned > 0 && (
                          <span className="text-orange-600 ml-2">(Returned: {returned})</span>
                        )}
                      </div>
                      {/* Show barcodes if available */}
                      {(autoBarcode || manualBarcode) && (
                        <div className="text-[10px] text-muted-foreground font-mono mt-0.5">
                          {autoBarcode && <span>Auto: {autoBarcode}</span>}
                          {autoBarcode && manualBarcode && <span className="mx-1">·</span>}
                          {manualBarcode && <span>Manual: {manualBarcode}</span>}
                        </div>
                      )}
                    </div>
                    <div className="font-mono-data font-medium">₹{subtotal.toFixed(2)}</div>
                  </div>
                );
              })}
              {lineItems.length === 0 && (
                <div className="px-3 py-4 text-center text-muted-foreground text-sm">
                  No items
                </div>
              )}
            </div>
          </div>

          {/* Totals */}
          <div className="bg-muted/30 rounded-lg p-3 space-y-2">
            <div className="flex justify-between text-sm">
              <span>Subtotal</span>
              <span className="font-mono-data">₹{total.toFixed(2)}</span>
            </div>
            {sale.bill_discount_pct > 0 && (
              <div className="flex justify-between text-sm text-green-600">
                <span>Discount ({sale.bill_discount_pct}%)</span>
                <span className="font-mono-data">-₹{(total * sale.bill_discount_pct / 100).toFixed(2)}</span>
              </div>
            )}
            {refundTotal > 0 && (
              <div className="flex justify-between text-sm text-orange-600">
                <span>Returns/Refunds</span>
                <span className="font-mono-data">-₹{refundTotal.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between font-semibold border-t pt-2">
              <span>Total</span>
              <span className="font-mono-data text-lg">₹{total.toFixed(2)}</span>
            </div>
          </div>

          {/* Payment Info */}
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-muted/50 px-3 py-2 text-xs font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              Payment Details
            </div>
            <div className="p-3 space-y-2">
              {payments.length > 0 ? (
                payments.map((p, idx) => (
                  <div key={`payment-${p.mode}-${idx}`} className="flex justify-between items-center text-sm">
                    <span className="flex items-center gap-2">
                      {getPaymentIcon(p.mode)}
                      <span className="capitalize">{p.mode}</span>
                    </span>
                    <span className="font-mono-data">₹{Number(p.amount || 0).toFixed(2)}</span>
                  </div>
                ))
              ) : (
                <div className="flex justify-between items-center text-sm">
                  <span className="flex items-center gap-2">
                    {getPaymentIcon(sale.payment_mode)}
                    <span className="capitalize">{sale.payment_mode || "—"}</span>
                  </span>
                  <span className="font-mono-data">₹{amountPaid.toFixed(2)}</span>
                </div>
              )}
              
              <div className="border-t pt-2 mt-2 flex justify-between text-sm">
                <span>Amount Paid</span>
                <span className="font-mono-data font-medium text-green-600">₹{amountPaid.toFixed(2)}</span>
              </div>
              
              {outstanding > 0.01 && (
                <div className="flex justify-between text-sm text-red-600">
                  <span>Outstanding</span>
                  <span className="font-mono-data font-medium">₹{outstanding.toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Returns Info (if any) */}
          {returns.length > 0 && (
            <div className="border border-orange-200 rounded-lg overflow-hidden bg-orange-50/30">
              <div className="bg-orange-100/50 px-3 py-2 text-xs font-medium uppercase tracking-wider text-orange-700 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Returns ({returns.length})
              </div>
              <div className="p-3 space-y-2 text-sm">
                {returns.map((ret, idx) => (
                  <div key={ret.id || `return-${idx}`} className="flex justify-between">
                    <span className="text-muted-foreground">
                      {formatDateTime(ret.returned_at)} - {ret.reason || "No reason"}
                    </span>
                    <span className="font-mono-data text-orange-600">-₹{Number(ret.refund_amount || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Cancellation Info */}
          {isCanceled && (
            <div className="border border-red-200 rounded-lg p-3 bg-red-50/30 text-sm">
              <div className="font-medium text-red-700 mb-1">Cancelled</div>
              <div className="text-muted-foreground">
                {sale.canceled_at && <div>At: {formatDateTime(sale.canceled_at)}</div>}
                {sale.canceled_by_name && <div>By: {sale.canceled_by_name}</div>}
                {sale.canceled_reason && <div>Reason: {sale.canceled_reason}</div>}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap items-center justify-between gap-2 pt-4 border-t">
          {/* Left side - Print & Share */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              data-testid="sale-detail-print-btn"
            >
              <Printer className="w-4 h-4 mr-1" />
              Print
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleWhatsApp}
              title={sale.customer_phone ? `Send receipt to ${sale.customer_phone}` : "Share via WhatsApp"}
              data-testid="sale-detail-whatsapp-btn"
            >
              <WhatsAppIcon className="w-4 h-4 mr-1" />
              WhatsApp
            </Button>
          </div>

          {/* Right side - Edit, Return, Cancel, Close */}
          <div className="flex items-center gap-2">
            {!isCanceled && onEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleEdit}
                data-testid="sale-detail-edit-btn"
              >
                <Pencil className="w-4 h-4 mr-1" />
                Edit
              </Button>
            )}
            {!isCanceled && onReturn && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleReturn}
                className="text-orange-600 border-orange-300 hover:bg-orange-50"
                data-testid="sale-detail-return-btn"
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                Return
              </Button>
            )}
            {!isCanceled && onCancel && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                className="text-red-600 border-red-300 hover:bg-red-50"
                data-testid="sale-detail-cancel-btn"
              >
                <XCircle className="w-4 h-4 mr-1" />
                Cancel
              </Button>
            )}
            <Button variant="secondary" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
